import moment from "moment";
import "moment/locale/sr";
import { DateTimePickerProps } from "./DateTime.types";
import { useClassNames, useTheme } from "@grassrootly/rn-core/src/structure";
import { useEffect, useMemo, useRef, useState } from "react";
import { View } from "react-native";
import Label from "../../basic/label/Label.web";

type LocaleAwareDateTimePickerProps = DateTimePickerProps & {
  locale?: string; // default 'sr'
  displayFormat?: string; // e.g. "DD.MM.YYYY"
};

export function DateTimePicker({
  value,
  minDate,
  maxDate,
  onChange,
  mode = "date",
  minuteInterval,
  dateDisplayFormat,
  label,
  isOptional,
  message,
  state,
  disabled,
  locale = "sr",
  displayFormat,
}: LocaleAwareDateTimePickerProps) {
  const style = useClassNames([
    "btnNeutralLighter",
    "bgSecondaryWashed",
    "l1Regular",
    "btnBlock",
    "rounded",
    "p8",
    "borderLight",
    "border2",
    "alignItemsCenter",
    "justifyContentCenter",
    "input",
    state ? "border" + state.charAt(0).toUpperCase() + state.slice(1) : "",
  ]);

  const { theme } = useTheme();
  const isoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    moment.locale(locale);
  }, [locale]);

  const inputType = mode === "datetime" ? "datetime-local" : mode === "time" ? "time" : "date";
  const isDate = inputType === "date";
  const isDateTime = inputType === "datetime-local";

  // Visible display format
  const displayFmt = useMemo(() => {
    if (dateDisplayFormat) return dateDisplayFormat;
    if (displayFormat) return displayFormat;
    switch (inputType) {
      case "time":
        return "HH:mm";
      case "datetime-local":
        return "DD.MM.YYYY HH:mm";
      default:
        return "DD.MM.YYYY";
    }
  }, [displayFormat, dateDisplayFormat, inputType]);

  const acceptedDisplayFormats = useMemo(() => {
    if (inputType === "time") return ["H:mm", "HH:mm"];
    if (inputType === "datetime-local")
      return [displayFmt, "D.M.YYYY H:mm", "DD.MM.YYYY HH:mm", "DD.MM.YYYY, HH:mm", "DD.MM.YY HH:mm"];
    return [displayFmt, "D.M.YYYY", "DD.MM.YYYY", "DD.MM.YY"];
  }, [displayFmt, inputType]);

  const [visibleValue, setVisibleValue] = useState(value ? moment(value).locale(locale).format(displayFmt) : "");
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setVisibleValue(value ? moment(value).locale(locale).format(displayFmt) : "");
    setHasError(false);
  }, [value, displayFmt, locale]);

  // ---- Helpers: native ISO (UTC for date-only) ----
  const toNativeIso = (d?: Date | string | null) => {
    if (!d) return "";
    const m = moment(d);
    if (inputType === "time") return m.format("HH:mm");
    if (isDateTime) return m.format("YYYY-MM-DDTHH:mm");
    return m.utc().format("YYYY-MM-DD");
  };

  const isoValue = value ? toNativeIso(value) : "";
  const isoMin = minDate ? toNativeIso(minDate) : undefined;
  const isoMax = maxDate ? toNativeIso(maxDate) : undefined;

  // Keep uncontrolled native input in sync with external value
  useEffect(() => {
    if (isoInputRef.current) isoInputRef.current.value = isoValue;
  }, [isoValue]);

  // ---- Open native picker (seed 00:00 when datetime & empty) ----
  const openingRef = useRef(false);
  const openNativePicker = () => {
    const el = isoInputRef.current;
    if (!el) return;

    // Seed a default time if datetime-local has no value yet
    if (isDateTime && !el.value) {
      const base = minDate ? moment(minDate) : moment(); // safe within/near constraints
      el.value = `${base.format("YYYY-MM-DD")}T00:00`;
    }

    openingRef.current = true;
    el.focus();
    if (typeof (el as any).showPicker === "function") (el as any).showPicker();
    else el.click();
    setTimeout(() => (openingRef.current = false), 0);
  };

  // ---- Parsing & validation helpers ----
  const snapMinute = (m: moment.Moment) => {
    if (minuteInterval && inputType !== "date") {
      const minutes = m.minutes();
      const snapped = Math.round(minutes / minuteInterval) * minuteInterval;
      m.minutes(snapped).seconds(0);
    }
  };

  const parseLocalized = (text: string): Date | null => {
    if (!text?.trim()) return null;
    const m = moment(text.trim(), acceptedDisplayFormats, locale, true);
    if (!m.isValid()) return null;
    snapMinute(m);
    return m.toDate();
  };

  const applyParsedIfValid = (parsed: Date | null) => {
    if (!parsed) return setHasError(true);
    if (minDate && parsed < new Date(minDate)) return setHasError(true);
    if (maxDate && parsed > new Date(maxDate)) return setHasError(true);
    setHasError(false);
    if (isoInputRef.current) isoInputRef.current.value = toNativeIso(parsed);
    onChange?.(parsed);
  };

  const isPlausiblePartial = (text: string) => {
    if (!text) return true;
    if (inputType === "time") return /^([0-2]?$|[0-2]\d$|[0-2]\d:$|[0-2]\d:[0-5]?$)/.test(text);
    if (inputType === "date")
      return /^(\d{0,2}|\d{1,2}\.?|\d{1,2}\.\d{0,2}|\d{1,2}\.\d{1,2}\.?|\d{1,2}\.\d{1,2}\.\d{0,4})$/.test(text);
    return /^(.{0,16})$/.test(text); // datetime-local partials
  };

  // ---- Visible input handlers ----
  const handleVisibleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setVisibleValue(text);
    if (isPlausiblePartial(text)) return setHasError(false);
    const parsed = parseLocalized(text);
    setHasError(!parsed);
  };

  const handleVisibleBlur = () => {
    if (openingRef.current) return; // ignore blur from opening picker
    if (!visibleValue.trim()) {
      setHasError(false);
      onChange?.(undefined as any);
      if (isoInputRef.current) isoInputRef.current.value = "";
      return;
    }
    const parsed = parseLocalized(visibleValue);
    if (!parsed) return setHasError(true);
    applyParsedIfValid(parsed);
    setVisibleValue(moment(parsed).locale(locale).format(displayFmt));
  };

  const handleVisibleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleVisibleBlur();
    }
  };

  // ---- Native parsing: inject 00:00 if datetime has no time ----
  const parseFromNative = (raw: string) => {
    if (!raw) {
      setVisibleValue("");
      setHasError(false);
      onChange?.(undefined as any);
      return;
    }

    // If browser returns only a date for datetime-local, append 00:00
    const normalized = isDateTime && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00` : raw;

    let m: moment.Moment;
    if (inputType === "time") m = moment(normalized, "HH:mm", true);
    else if (isDateTime) m = moment(normalized, "YYYY-MM-DDTHH:mm", true);
    else m = moment.utc(normalized, "YYYY-MM-DD", true);

    if (!m.isValid()) return;

    snapMinute(m);
    const d = m.toDate();
    setVisibleValue(m.locale(locale).format(displayFmt));
    setHasError(false);
    onChange?.(d);
  };

  // React handlers (some engines dispatch only one of these)
  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement> | React.FormEvent<HTMLInputElement>) => {
    parseFromNative((e.target as HTMLInputElement).value);
  };

  // Native listeners to catch all engines
  useEffect(() => {
    const el = isoInputRef.current;
    if (!el) return;
    const handler = (ev: Event) => parseFromNative((ev.target as HTMLInputElement).value);
    el.addEventListener("input", handler);
    el.addEventListener("change", handler);
    return () => {
      el.removeEventListener("input", handler);
      el.removeEventListener("change", handler);
    };
  }, [inputType, locale, displayFmt]);

  return (
    <View style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <View style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
        {label ? <Label type="l2Regular">{label}</Label> : null}
        {isOptional ? (
          <Label color={theme.colors.neutralDarker.contrast} type="l2Regular">
            Optional
          </Label>
        ) : null}
      </View>

      <View style={{ position: "relative", width: "100%" }}>
        <View style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
          {/* Visible text field */}
          <input
            type="text"
            disabled={disabled}
            value={visibleValue}
            onChange={handleVisibleChange}
            onBlur={handleVisibleBlur}
            onKeyDown={handleVisibleKeyDown}
            placeholder={
              inputType === "time" ? "hh:mm" : inputType === "datetime-local" ? "dd.mm.yyyy hh:mm" : "dd.mm.yyyy"
            }
            inputMode={inputType === "time" ? "numeric" : "text"}
            aria-invalid={hasError || undefined}
            aria-describedby={message ? "dtp-msg" : undefined}
            style={{
              ...style,
              cursor: disabled ? "not-allowed" : "text",
              colorScheme: "dark",
              width: "100%",
              flex: 1,
              paddingRight: 36,
              outline: hasError ? `2px solid ${theme.colors.danger.main}` : undefined,
            }}
          />

          {/* Native input (UNCONTROLLED), tiny & invisible but interactive */}
          <input
            ref={isoInputRef}
            type={inputType}
            disabled={disabled}
            defaultValue={isoValue}
            min={isoMin}
            max={isoMax}
            onChange={handleNativeChange}
            onInput={handleNativeChange}
            style={{ position: "absolute", opacity: 0, width: 1, height: 1 }}
            aria-hidden="true"
            tabIndex={-1}
          />

          {/* Button to open picker */}
          <button
            type="button"
            onClick={openNativePicker}
            disabled={disabled}
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "transparent",
              border: "none",
              cursor: disabled ? "not-allowed" : "pointer",
              width: 24,
              height: 24,
            }}
            aria-label={inputType === "time" ? "Otvori izbor vremena" : "Otvori kalendar"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill={disabled ? "#aaa" : "#4a90e2"}
            >
              <path
                d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 
      0-2 .9-2 2v14c0 1.1.9 2 
      2 2h14c1.1 0 2-.9 
      2-2V6c0-1.1-.9-2-2-2zm0 
      16H5V9h14v11zm0-13H5V6h14v1z"
              />
            </svg>
          </button>
        </View>
      </View>

      {message ? (
        <Label id="dtp-msg" type="l3Regular" color={theme.colors[state || "neutralDarker"].main}>
          {message}
        </Label>
      ) : null}
    </View>
  );
}
